import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import { localizeArt, anonymousLang} from './../localize.js';
import { formatYear } from './../utils.js';
import { Button, Loader, Paper, Text } from '@mantine/core';

// Lists a user's reviews sorted by updatedTime DESC. Each card shows
// the art thumbnail + title, the review body (rendered markdown,
// clamped to a preview height), and a "read more" link to the
// dedicated review page.
// `initialItems` / `onLoaded` let the parent cache the fetched list
// across tab remounts so switching Library → Reviews → Library →
// Reviews doesn't refetch every time.
const UserReviewsList = ({ userId, initialItems = null, onLoaded, query = '' }) => {
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : anonymousLang();
  const term = query.trim();
  const [items, setItems] = useState(term ? null : initialItems);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!userId) return undefined;
    const controller = new AbortController();
    setError(false);
    // Only the unfiltered list belongs in the parent's tab cache.
    if (!term && initialItems !== null) { setItems(initialItems); return undefined; }
    setItems(null);
    const timer = setTimeout(() => {
      axiosInstance.get(`/users/${userId}/reviews`, { params: { q: term }, signal: controller.signal })
        .then(({ data }) => {
          if (controller.signal.aborted) return;
          const next = Array.isArray(data) ? data : [];
          setItems(next);
          if (!term && onLoaded) onLoaded(next);
        })
        .catch(() => { if (!controller.signal.aborted) setError(true); });
    }, term ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [userId, term, initialItems, onLoaded, retry]);

  if (error) {
    return <div className="text-center mt-8">
      <Text c="dimmed">{language === 'ru' ? 'Не удалось загрузить отзывы.' : 'Could not load reviews.'}</Text>
      <Button variant="subtle" onClick={() => setRetry(n => n + 1)}>{language === 'ru' ? 'Повторить' : 'Retry'}</Button>
    </div>;
  }

  if (items === null) {
    return (
      <div className="flex justify-center mt-8">
        <Loader size="sm" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex justify-center mt-8">
        <Text c="dimmed">
          {term ? (language === 'ru' ? 'Отзывы не найдены' : 'No reviews found') : (language === 'ru' ? 'Пока нет отзывов' : 'No reviews yet')}
        </Text>
      </div>
    );
  }

  const dt = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(language, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  return (
    <div className="flex flex-col gap-3 mt-2">
      {items.map(({ review, art, artEventId }) => {
        if (!art) return null;
        const localized = localizeArt(art, language);
        const displayName = (localized?.name || art.name || art.ruName || '') + formatYear(art.year, language);
        const readFullTo = artEventId ? `/artEvents/${artEventId}` : `/reviews/${review.id}`;
        return (
          <Paper key={review.id} shadow="xs" radius="md" p="md" style={{ backgroundColor: 'var(--app-surface)' }}>
            <div className="flex items-start gap-3 mb-2">
              <Link to={`/arts/${art.id}`}>
                <img
                  src={art.thumbnailUrl}
                  alt=""
                  className="rounded-md w-[56px] h-[76px] object-cover"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/arts/${art.id}`}>
                  <Text size="md" fw={700}>{displayName}</Text>
                </Link>
                <Text size="xs" c="dimmed">
                  {(language === 'ru' ? 'Обновлено ' : 'Updated ') + dt(review.updatedTime)}
                </Text>
              </div>
            </div>
            <div
              style={{
                maxHeight: 220,
                overflow: 'hidden',
                position: 'relative',
                lineHeight: 1.6,
                fontSize: '0.95rem',
                maskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(review.text || '') }}
            />
            <div className="mt-2">
              <Link
                to={readFullTo}
                style={{ color: 'var(--mantine-color-blue-text)', fontSize: '0.85rem' }}
              >
                {language === 'ru' ? 'Читать полностью →' : 'Read full →'}
              </Link>
            </div>
          </Paper>
        );
      })}
    </div>
  );
};

export default UserReviewsList;
